import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { To } from 'react-router';

import {
  ManagerProgressionTaskSlug,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import {
  ANY_SPORT_MISSIONS_CATEGORY,
  ANY_SPORT_PLAY_BOARDS_PRO_BOARD,
  ANY_SPORT_PLAY_QUICK_PLAY,
  ANY_SPORT_SERIES_PLAY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';

import { DumbNotification } from '#src/components/activity/DumbNotification/index.tsx';
import { commonNotificationInterfaceFragment } from '#src/components/activity/Notification/fragments.ts';
import type { CommonNotificationProps } from '#src/components/activity/Notification/types.ts';

import type { TaskNotification_taskNotification } from './__generated__/index.graphql.ts';

type Props = CommonNotificationProps & {
  notification: TaskNotification_taskNotification;
};

export const TaskNotification = ({ notification, ...rest }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { createdAt, name, sport, read, task } = notification;
  const {
    flags: {
      useQuickPlay = {
        [Sport.BASEBALL]: false,
        [Sport.NBA]: false,
        [Sport.FOOTBALL]: false,
      },
    },
  } = useFeatureFlags();

  let link: To;
  if (isType(task, 'ThresholdPickerTask')) {
    if (
      task.name === ManagerProgressionTaskSlug.PRO_THRESHOLD_PICKER &&
      task.parentTask
    ) {
      link = generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO_BOARD, {
        params: { board: idFromObject(task.parentTask.id) },
      });
    } else {
      link = generateSportContextPath(ANY_SPORT_SERIES_PLAY);
    }
  } else if (
    task.parentTask &&
    isType(task.parentTask, 'TasksTrack') &&
    useQuickPlay[sport || Sport.FOOTBALL]
  ) {
    link = generateSportContextPath(ANY_SPORT_PLAY_QUICK_PLAY);
  } else {
    link = generateSportContextPath(ANY_SPORT_MISSIONS_CATEGORY, {
      params: { category: 'play' },
    });
  }

  return (
    <DumbNotification
      name={name}
      title={<LazyMarkdown data={notification.markdownBody} />}
      link={link}
      createdAt={createdAt}
      sport={sport}
      read={read}
      cardPicture={
        <img width={40} height={40} src={task.templateIconUrl} alt="" />
      }
      {...rest}
    />
  );
};

TaskNotification.fragments = {
  taskNotification: gql`
    fragment TaskNotification_taskNotification on TaskNotification {
      id
      task {
        id
        periodicity
        templateIconUrl
        parentTask {
          id
        }
        name
      }
      ...Notification_notificationInterface
    }
    ${commonNotificationInterfaceFragment}
  ` as TypedDocumentNode<TaskNotification_taskNotification>,
};
